import React, { ReactElement, useCallback, useEffect, useState } from 'react'
import { EnabledStatus, RuleType } from 'store/api/rules'
import BlockIcon from 'images/block-icon-log.svg'
import WhitelistIcon from 'images/whitelist-icon-log.svg'
import RedirectIcon from 'images/redirect-icon.svg'
import RedirectButton from 'components/Dashboard/Profiles/RuleActions/RedirectButton'
import { ActionableRule, isCustomRule, isServiceRule } from 'utils/uniteRules'
import { ServiceData } from 'store/api/services'
import { ProxyLocation } from 'store/api/proxies'
import ActionButtonTooltip, { tooltipVisibility } from './ActionButtonTooltip'
import { setRuleTooltipSettings } from 'store/tutorial/tutorial'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import useBoundaryElement from 'utils/useBoundaryElement'
import useGetUser from 'components/Dashboard/utils/useGetUser'
import ActionButtonsContainer from '../RuleActionButtons/ActionButtonsContainer'
import ActionButton, { ActionType } from '../RuleActionButtons/ActionButton'
import { Flex } from 'theme-ui'

interface RuleActionProps {
  rule: ActionableRule
  handleRuleTypeClick: (ruleType: RuleType) => void
  setRedirectButtonHover?: (x: boolean) => void
  /**
   * Used for forcing a custom rule to have the folder rule type
   */
  forceRuleType?: RuleType
  redirectLocation?: ProxyLocation
}
export default function RuleActions({
  rule,
  handleRuleTypeClick,
  setRedirectButtonHover,
  forceRuleType,
  redirectLocation,
}: RuleActionProps): ReactElement {
  const dispatch = useAppDispatch()
  const { data: userData } = useGetUser()
  const userPk = userData?.PK || ''
  const viewedStateByUserPk = useAppSelector(s => s.tutorial.viewedStateByUserPk[userPk ?? ''])
  const selectedRuleType = viewedStateByUserPk?.selectedRuleType
  const isBlocked = (forceRuleType ?? rule?.action?.do) === RuleType.BLOCK
  const isWhiteListed = (forceRuleType ?? rule?.action?.do) === RuleType.WHITELIST
  const isRedirect =
    (forceRuleType ?? rule?.action?.do) === RuleType.SPOOF_TAG ||
    (forceRuleType ?? rule?.action?.do) === RuleType.SPOOF_IP
  const isSwitchChecked = rule?.action?.status === EnabledStatus.ENABLED || isCustomRule(rule)
  const [isTooltipVisible, setIsTooltipVisible] = useState(false)
  const boundaryElement = useBoundaryElement('dashboard-services')
  // check if the rule has a ServiceData type
  const serviceName = 'name' in rule ? rule.name : ''
  const isServiceActionRule = isServiceRule(rule)
  const isTooltipEnabled = isServiceActionRule
    ? rule.PK === viewedStateByUserPk?.enabledServicePk
    : rule.PK === viewedStateByUserPk?.selectedRuleOrFolderPk

  useEffect(() => {
    setIsTooltipVisible(isTooltipEnabled)
  }, [dispatch, isTooltipEnabled])

  const onHideOrClick = useCallback(() => {
    if (selectedRuleType !== undefined) {
      dispatch(
        setRuleTooltipSettings({
          userPk,
          [tooltipVisibility[selectedRuleType]]: true,
        }),
      )
    }
  }, [dispatch, userPk, selectedRuleType])

  const onClick = (ruleType: RuleType) => {
    if (forceRuleType) {
      return
    }

    onHideOrClick()
    handleRuleTypeClick(ruleType)
  }

  return (
    <ActionButtonTooltip
      ariaLabel="action button tooltip"
      appendTo={boundaryElement}
      isVisible={isTooltipVisible}
      cityName={redirectLocation?.city}
      serviceName={serviceName}
      onHideOrClick={onHideOrClick}
    >
      <Flex sx={{ justifyContent: 'flex-end', flexShrink: 0 }}>
        <ActionButtonsContainer
          sxContainer={{
            alignSelf: isServiceActionRule ? 'flex-end' : 'unset',
            backgroundColor: isSwitchChecked && forceRuleType === undefined ? 'blue800' : 'white6',
          }}
        >
          <ActionButton
            isService={isServiceActionRule}
            disabled={forceRuleType !== undefined}
            type={ActionType.BLOCK}
            icon={BlockIcon}
            isActive={isBlocked}
            isSwitchChecked={isSwitchChecked}
            onClick={(): void => onClick(RuleType.BLOCK)}
          />
          <ActionButton
            isService={isServiceActionRule}
            onClick={(): void => onClick(RuleType.WHITELIST)}
            type={ActionType.BYPASS}
            isActive={isWhiteListed}
            isSwitchChecked={isSwitchChecked}
            icon={WhitelistIcon}
            disabled={forceRuleType !== undefined}
          />
          {isServiceActionRule ? (
            <RedirectButton
              serviceRule={rule as ServiceData}
              isSwitchChecked={isSwitchChecked}
              setRedirectButtonHover={setRedirectButtonHover}
              onFirstClick={(): void => onClick(RuleType.SPOOF_TAG)}
              redirectLocation={redirectLocation}
            />
          ) : (
            <ActionButton
              onClick={(): void => {
                if (forceRuleType) {
                  return
                }

                if (
                  rule?.action?.do !== RuleType.SPOOF_IP &&
                  rule?.action?.do !== RuleType.SPOOF_TAG
                ) {
                  onClick(RuleType.SPOOF_TAG)
                }
              }}
              type={ActionType.REDIRECT}
              isActive={isRedirect}
              isSwitchChecked={isSwitchChecked}
              icon={RedirectIcon}
              disabled={forceRuleType !== undefined}
            />
          )}
        </ActionButtonsContainer>
      </Flex>
    </ActionButtonTooltip>
  )
}
